import type { FieldDefinition, Locale } from '@sentra/contracts';
import { countryName, formatPhone } from '@sentra/contracts';
import type { ReactNode } from 'react';

import type { Formatters } from '@/i18n/format';

/**
 * One decrypted value, rendered the way its field's TYPE says it should be.
 *
 * **Rendered from the definition rather than from the value's JavaScript type.** A date arrives as
 * `2026-09-11` and a passport number arrives as a string too; only the registry knows that one is a
 * date to be formatted in the building's timezone and the other is a document number to be left
 * exactly as it was stored. Guessing from the value is how a document number that happens to look
 * like a date gets re-rendered as one.
 *
 * **A choice is rendered through its own bilingual label.** The value stored is the machine key —
 * that is what is indexed and compared — and the label is what a person reads; a screen that showed
 * the key would show `pulsera_rota` to somebody who has never seen this field's definition.
 *
 * **A missing value is an em dash and never an empty cell.** "This field has no value" and "this
 * screen failed to read it" look identical when both are blank, and on a record screen the first is
 * ordinary and the second is a defect.
 */
export function FieldValue({
  definition,
  value,
  locale,
  format,
}: Readonly<{
  definition: FieldDefinition;
  value: unknown;
  locale: Locale;
  format: Formatters;
}>): ReactNode {
  const rendered = renderValue(definition, value, locale, format);
  return rendered === null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className={definition.type === 'passport' ? 'font-mono' : undefined}>{rendered}</span>
  );
}

/** The text one value becomes, or `null` when there is nothing stored. */
function renderValue(
  definition: FieldDefinition,
  value: unknown,
  locale: Locale,
  format: Formatters,
): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  switch (definition.type) {
    case 'boolean': {
      return value === true ? '✓' : '✗';
    }
    case 'date': {
      return typeof value === 'string' ? format.date(`${value}T00:00:00Z`) : null;
    }
    case 'country': {
      return typeof value === 'string' ? countryName(value, locale) : null;
    }
    case 'number': {
      return typeof value === 'number' ? format.decimal(value) : null;
    }
    case 'select': {
      return optionLabel(definition, value, locale);
    }
    case 'multiselect': {
      if (!Array.isArray(value)) {
        return null;
      }
      const chosen = value as readonly unknown[];
      return format.list(
        chosen.flatMap((entry) => {
          const label = optionLabel(definition, entry, locale);
          return label === null ? [] : [label];
        }),
      );
    }
    case 'phone': {
      // Grouped for reading, which is where grouping belongs: `+34 612 34 56 78` is checkable
      // against a document at a glance and `+34612345678` is not. The form deliberately does the
      // opposite and shows plain digits, because regrouping as somebody types moves the caret under
      // them. A number the plans cannot read is returned as it stands, so a record still shows what
      // it holds.
      const stored = storedText(value);
      return stored === null ? null : formatPhone(stored);
    }
    case 'text':
    case 'textarea':
    case 'email':
    case 'passport': {
      return storedText(value);
    }
  }
}

/**
 * A stored value as the text it is, or `null` for anything that is not text.
 *
 * Shared by the five types that hold a string, so the narrowing is written once: a record that
 * somehow holds an object for a text field renders as missing and is re-entered, rather than as
 * `[object Object]` in front of an operator.
 */
function storedText(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

/** The bilingual label of one choice, or `null` for a value the definition no longer offers. */
function optionLabel(definition: FieldDefinition, value: unknown, locale: Locale): string | null {
  if (definition.validation.type !== 'select' && definition.validation.type !== 'multiselect') {
    return null;
  }
  const option = definition.validation.options.find((candidate) => candidate.value === value);
  // A value whose option has since been removed is shown as the stored key rather than dropped: it
  // is what is in the record, and hiding it would make the screen disagree with the export.
  return option?.label[locale] ?? (typeof value === 'string' ? value : null);
}
